import { useMemo, useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  getFirebaseClient,
  useAuth,
  consultationConditionLabel,
  parseConsultationListRow,
  type ConsultationListRow,
} from "@workspace/admin-ui";
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import { PageHeader } from "@/components/AppShell";

// Task #438 — Pharmacy Admin Consultations tab. Lists completed
// consultations synced from pharmacist devices at this pharmacy (see
// artifacts/ccs-pharmacy/src/services/firebase/consultationSync.ts for the
// mobile-side mirror write, and firestore.rules for the read scope).
export default function ConsultationsPage() {
  const { membership } = useAuth();
  const chainId = membership?.chainId ?? "";
  const pharmacyId = membership?.pharmacyId ?? "";
  const { db } = getFirebaseClient();
  const [search, setSearch] = useState("");

  const consultationsQ = useQuery<ConsultationListRow[]>({
    queryKey: ["consultations", chainId, pharmacyId],
    queryFn: async () => {
      const snap = await getDocs(
        query(
          collection(db, "consultations"),
          where("chainId", "==", chainId),
          where("pharmacyId", "==", pharmacyId),
          orderBy("createdAt", "desc"),
        ),
      );
      return snap.docs.map((d) => parseConsultationListRow(d.id, d.data()));
    },
    enabled: !!chainId && !!pharmacyId,
  });

  const rows = consultationsQ.data ?? [];
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (r) =>
        r.patientName.toLowerCase().includes(term) ||
        r.pharmacistName.toLowerCase().includes(term) ||
        consultationConditionLabel(r.condition).toLowerCase().includes(term),
    );
  }, [rows, search]);

  return (
    <>
      <PageHeader
        title="Consultations"
        description="Completed consultations synced from pharmacist devices at this pharmacy."
      />
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Paper>
          <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
            <TextField
              placeholder="Search by patient, pharmacist, or condition"
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          {consultationsQ.isLoading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <CircularProgress size={20} />
            </Box>
          ) : consultationsQ.error ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="error">
                {(consultationsQ.error as Error).message}
              </Typography>
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">
                {rows.length === 0
                  ? "No consultations synced yet."
                  : "No consultations match your search."}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filtered.map((c, i) => (
                <Box key={c.id}>
                  <ListItemButton
                    component={Link}
                    href={`/consultations/${c.id}`}
                    sx={{ py: 1.5, px: 2 }}
                  >
                    <ListItemText
                      primary={
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: "center", flexWrap: "wrap" }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {c.patientName || "Unnamed patient"}
                          </Typography>
                          <Chip
                            label={consultationConditionLabel(c.condition)}
                            size="small"
                            variant="outlined"
                          />
                        </Stack>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          Pharmacist: {c.pharmacistName || c.pharmacistUid} ·
                          Completed{" "}
                          {c.completedAt
                            ? new Date(c.completedAt).toLocaleString()
                            : "—"}
                        </Typography>
                      }
                    />
                    <ChevronRightIcon fontSize="small" color="disabled" />
                  </ListItemButton>
                  {i < filtered.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          )}
        </Paper>
      </Box>
    </>
  );
}
